import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MessageSquare,
  Layers,
  Zap,
  Palette,
  Code,
  Briefcase,
  Settings,
  Target,
  Lightbulb,
  TrendingUp,
  Wrench,
  BookOpen,
  ArrowRight,
  Check,
  Sparkles,
  Terminal,
  X,
  Send,
  Loader
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= REGISTRATION MODAL =============
const RegistrationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    experience: '',
    goals: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.role) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'Prompt Engineering Workshop - 5-Day Intensive',
          message: `Prompt Engineering Workshop Registration\n\nRole: ${formData.role}\nAI Experience: ${formData.experience || 'Not specified'}\n\nWhat they want to achieve:\n${formData.goals || 'Not specified'}`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Registration submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', role: '', experience: '', goals: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', role: '', experience: '', goals: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Registration Complete!</h3>
            <p className="text-gray-400 mb-6">
              Welcome to the Prompt Engineering Workshop! We'll send you the workshop details and access information within 24 hours.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Terminal size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">5-Day Workshop</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Register Now</h2>
            <p className="text-teal-400 mb-6">Prompt Engineering Mastery Workshop</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Your Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select your role</option>
                  <option value="Content Creator / Writer" className="bg-slate-950">Content Creator / Writer</option>
                  <option value="Software Developer" className="bg-slate-950">Software Developer</option>
                  <option value="Designer / Artist" className="bg-slate-950">Designer / Artist</option>
                  <option value="Marketing Professional" className="bg-slate-950">Marketing Professional</option>
                  <option value="Business Analyst" className="bg-slate-950">Business Analyst</option>
                  <option value="Product Manager" className="bg-slate-950">Product Manager</option>
                  <option value="Entrepreneur" className="bg-slate-950">Entrepreneur</option>
                  <option value="Student" className="bg-slate-950">Student</option>
                  <option value="Other" className="bg-slate-950">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">AI Tools Experience</label>
                <select
                  value={formData.experience}
                  onChange={(e) => handleChange('experience', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select experience level</option>
                  <option value="Beginner - Just started using AI" className="bg-slate-950">Beginner - Just started using AI</option>
                  <option value="Intermediate - Use AI regularly" className="bg-slate-950">Intermediate - Use AI regularly</option>
                  <option value="Advanced - Heavy AI user" className="bg-slate-950">Advanced - Heavy AI user</option>
                  <option value="Expert - Building with AI APIs" className="bg-slate-950">Expert - Building with AI APIs</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">What do you want to achieve?</label>
                <textarea
                  value={formData.goals}
                  onChange={(e) => handleChange('goals', e.target.value)}
                  rows="2"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Better content, faster coding, creative work..."
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Register for Workshop
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onRegister }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-teal-400 transition">Home</Link>
            <a href="#modules" className="text-sm text-gray-300 hover:text-teal-400 transition">Modules</a>
            <a href="#benefits" className="text-sm text-gray-300 hover:text-teal-400 transition">Benefits</a>
            <a href="#journey" className="text-sm text-gray-300 hover:text-teal-400 transition">Journey</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onRegister}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Register Now
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-80 h-80 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-80 h-80 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-80 h-80 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onRegister }) => {
  const stats = [
    { number: "7", label: "Core Techniques" },
    { number: "100+", label: "Prompt Templates" },
    { number: "5X", label: "Output Quality" },
    { number: "Live", label: "Practice Sessions" }
  ];

  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-20 bg-slate-950">
      <GradientOrbs />

      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.15),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badge */}
        <motion.div
          className="mb-6 inline-flex items-center gap-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 backdrop-blur-sm flex items-center gap-2">
            <Terminal size={14} />
            5-DAY WORKSHOP
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="block bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Prompt Engineering
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-teal-300 mb-4 font-medium"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Master the Art of AI Communication
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-lg text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Unlock the full potential of AI models through advanced prompt engineering techniques. Learn to craft 
          precise, effective prompts that generate exceptional results from ChatGPT, Claude, Midjourney, and other 
          AI systems. Transform from casual user to prompt architect.
        </motion.p>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 hover:border-teal-500/30 transition-all duration-300"
              whileHover={{ y: -4, scale: 1.02 }}
            >
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent mb-1">
                {stat.number}
              </div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(20, 184, 166, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={onRegister}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold shadow-[0_0_30px_rgba(20,184,166,0.2)] transition flex items-center gap-2"
          >
            Register Now <ArrowRight size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('modules').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-500/40"
          >
            View Curriculum
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= MODULE CARD =============
const ModuleCard = ({ module, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-50px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-emerald-500/10 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 hover:border-teal-500/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-teal-500/50 to-transparent" />

        <div className="relative z-10 flex flex-col h-full">
          <div className="mb-4 inline-flex items-center gap-2">
            <span className="text-xs font-bold text-teal-400 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20">
              Session {String(index + 1).padStart(2, '0')}
            </span>
          </div>

          <motion.div
            className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20 group-hover:from-blue-500/30 group-hover:to-emerald-500/30 transition-all duration-500"
            animate={isHovered ? { rotate: [0, -10, 10, -10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <module.icon className="text-teal-300 group-hover:text-teal-200 transition" size={28} />
          </motion.div>

          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all duration-300">
            {module.title}
          </h3>

          <p className="text-gray-400 mb-6 leading-relaxed text-sm flex-grow">
            {module.description}
          </p>

          <ul className="space-y-2">
            {module.topics.map((topic, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-500 flex items-start gap-2"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
              >
                <Check size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{topic}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= MODULES SECTION =============
const ModulesSection = () => {
  const modules = [
    {
      icon: MessageSquare,
      title: "Prompt Fundamentals",
      description: "Master the core principles of effective prompting. Understand how AI models interpret instructions and learn the anatomy of high-performing prompts.",
      topics: [
        "How AI language models process prompts",
        "The 4 pillars of effective prompts",
        "Common mistakes and how to avoid them",
        "Clarity vs creativity in prompt design"
      ]
    },
    {
      icon: Layers,
      title: "Context Engineering",
      description: "Learn to provide optimal context and constraints that guide AI to generate exactly what you need with precision and consistency.",
      topics: [
        "Role assignment and persona prompting",
        "Context setting and background information",
        "Constraint specification techniques",
        "Output format and structure control"
      ]
    },
    {
      icon: Zap,
      title: "Advanced Techniques",
      description: "Unlock powerful advanced methods including chain-of-thought prompting, few-shot learning, and prompt chaining for complex tasks.",
      topics: [
        "Chain-of-thought reasoning prompts",
        "Few-shot and zero-shot prompting",
        "Prompt chaining for multi-step tasks",
        "Temperature and parameter optimization"
      ]
    },
    {
      icon: Palette,
      title: "Creative Prompting",
      description: "Master prompts for creative applications including content generation, storytelling, and ideation across text and image AI.",
      topics: [
        "Creative writing and storytelling prompts",
        "Marketing copy and content creation",
        "Image generation prompt architecture",
        "Style transfer and artistic direction"
      ]
    },
    {
      icon: Code,
      title: "Technical Prompting",
      description: "Craft prompts for code generation, debugging, data analysis, and technical documentation with precision and efficiency.",
      topics: [
        "Code generation and refactoring prompts",
        "Debugging and error analysis techniques",
        "API and documentation prompts",
        "Data analysis and visualization requests"
      ]
    },
    {
      icon: Briefcase,
      title: "Business Applications",
      description: "Apply prompt engineering to business scenarios including analysis, strategy, customer service, and workflow automation.",
      topics: [
        "Business analysis and reporting prompts",
        "Customer service response templates",
        "Meeting summaries and action items",
        "Strategic planning and decision support"
      ]
    },
    {
      icon: Settings,
      title: "Optimization Mastery",
      description: "Learn iterative refinement, A/B testing prompts, and building reusable prompt libraries for consistent excellence.",
      topics: [
        "Iterative prompt refinement strategies",
        "Testing and evaluating prompt performance",
        "Building personal prompt libraries",
        "Scaling prompts across teams"
      ]
    }
  ];

  return (
    <section id="modules" className="relative py-24 px-4 overflow-hidden bg-slate-950">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[300px] bg-gradient-to-b from-teal-950/20 to-transparent" />
      <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-blue-500/[0.03] rounded-full blur-[120px]" />
      <div className="absolute bottom-40 left-0 w-[300px] h-[300px] bg-emerald-600/[0.03] rounded-full blur-[100px]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Workshop Modules
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Prompt Mastery{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Curriculum</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Comprehensive training in prompt engineering, from foundational principles to advanced techniques. 
            Master the skill that multiplies your AI productivity and unlocks creative possibilities.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {modules.slice(0, 4).map((module, index) => (
            <ModuleCard key={index} module={module} index={index} />
          ))}
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 max-w-5xl mx-auto">
          {modules.slice(4).map((module, index) => (
            <ModuleCard key={index + 4} module={module} index={index + 4} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= BENEFITS SECTION =============
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Zap,
      title: "10X Productivity",
      description: "Transform hours of work into minutes. Master prompts that generate exactly what you need on the first try, eliminating endless iterations and frustration."
    },
    {
      icon: Target,
      title: "Precision Control",
      description: "Guide AI to produce specific outputs with exact tone, style, format, and content. Move from generic responses to perfectly tailored results every time."
    },
    {
      icon: Lightbulb,
      title: "Creative Leverage",
      description: "Unlock creative possibilities you never imagined. Use AI as a creative partner that amplifies your ideas and helps you explore new directions."
    },
    {
      icon: TrendingUp,
      title: "Career Advantage",
      description: "Prompt engineering is in explosive demand. Companies pay premium rates for professionals who can maximize AI tool effectiveness and train teams."
    },
    {
      icon: Wrench,
      title: "Tool Mastery",
      description: "Work effectively across all major AI platforms - ChatGPT, Claude, Midjourney, Stable Diffusion, and more. Universal skills that apply everywhere."
    },
    {
      icon: BookOpen,
      title: "Template Library",
      description: "Build a personal library of tested, proven prompts for every situation. Never start from scratch again with our 100+ professional templates."
    }
  ];

  return (
    <section id="benefits" className="relative py-24 px-4 overflow-hidden bg-slate-900/50">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(20,184,166,0.05),transparent_70%)]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Workshop Benefits
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Why Master{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Prompt Engineering?</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Prompt engineering is the most valuable AI skill today. It multiplies the effectiveness of every 
            AI tool you use and opens doors to opportunities others can't access.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 hover:border-teal-500/30 transition-all duration-300 h-full">
                <motion.div
                  className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20 group-hover:from-blue-500/30 group-hover:to-emerald-500/30 transition-all"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <benefit.icon className="text-teal-300 group-hover:text-teal-200 transition" size={26} />
                </motion.div>

                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-teal-400 transition-all">
                  {benefit.title}
                </h3>

                <p className="text-gray-400 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= JOURNEY SECTION (Timeline) =============
const JourneySection = () => {
  const stages = [
    {
      title: "Foundation Building",
      description: "Learn core principles, understand AI model behavior, and craft your first effective prompts. Master clarity, context, and constraint specification techniques."
    },
    {
      title: "Technique Mastery",
      description: "Deploy advanced techniques like chain-of-thought, few-shot learning, and prompt chaining. Practice with real scenarios across creative, technical, and business applications."
    },
    {
      title: "Specialized Skills",
      description: "Master domain-specific prompting for your field - whether creative, technical, or business. Build custom templates and workflows that solve your unique challenges."
    },
    {
      title: "Expert Level",
      description: "Achieve prompt engineering mastery with optimization strategies, personal libraries, and ability to train others. Become the go-to AI expert in your organization."
    }
  ];

  return (
    <section id="journey" className="relative py-24 px-4 overflow-hidden bg-slate-950">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-teal-500/[0.03] rounded-full blur-[150px]" />

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Learning Path
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Your Prompt Mastery{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Journey</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Progressive learning path from basic prompts to advanced architectures. Each session builds 
            skills with hands-on practice and real-world applications.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-8 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-blue-500 via-teal-500 to-emerald-500" />

          {stages.map((stage, index) => (
            <motion.div
              key={index}
              className={`relative flex items-start gap-8 mb-12 last:mb-0 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              }`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 border-4 border-slate-950 z-10" />

              <div className={`ml-16 md:ml-0 md:w-[calc(50%-2rem)] ${index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                <motion.div
                  className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300"
                  whileHover={{ y: -4 }}
                >
                  <div className={`mb-3 ${index % 2 === 0 ? 'md:flex md:justify-end' : ''}`}>
                    <span className="text-xs font-bold text-teal-400 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20">
                      Day {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">
                    {stage.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {stage.description}
                  </p>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= WHO IS THIS FOR SECTION =============
const WhoIsThisForSection = () => {
  const audiences = [
    { emoji: "✍️", title: "Content Creators", desc: "Writers, marketers, and creatives" },
    { emoji: "👨‍💻", title: "Developers", desc: "Engineers using AI for coding" },
    { emoji: "📊", title: "Business Pros", desc: "Analysts and strategists" },
    { emoji: "🎨", title: "Designers", desc: "Visual artists using AI tools" }
  ];

  return (
    <section className="relative py-20 px-4 overflow-hidden bg-slate-900/50">
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Perfect For Anyone Using AI
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {audiences.map((audience, index) => (
            <motion.div
              key={index}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 text-center hover:border-teal-500/30 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
            >
              <div className="text-4xl mb-3">{audience.emoji}</div>
              <h3 className="text-white font-semibold mb-2">{audience.title}</h3>
              <p className="text-gray-500 text-sm">{audience.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onRegister }) => {
  return (
    <section className="relative py-28 px-6 overflow-hidden bg-slate-950">
      <div className="max-w-3xl mx-auto">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[300px] bg-gradient-to-r from-blue-500/10 via-teal-500/10 to-emerald-500/10 rounded-full blur-[100px]" />
        </div>

        <motion.div
          className="relative text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20"
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <Sparkles className="text-teal-300" size={40} />
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 tracking-tight">
            Join the{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Workshop</span>
          </h2>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto leading-relaxed">
            Transform your AI interactions from trial-and-error to precision engineering. Master the skill that unlocks AI's full potential.
          </p>

          {/* What's Included */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {["5 Live Sessions", "100+ Templates", "Hands-on Practice", "Certificate"].map((item, i) => (
              <span key={i} className="text-sm text-gray-400 flex items-center gap-2">
                <Check size={16} className="text-emerald-400" />
                {item}
              </span>
            ))}
          </div>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(20, 184, 166, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={onRegister}
              className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 text-white rounded-full font-semibold text-lg hover:from-blue-400 hover:via-teal-400 hover:to-emerald-400 transition-all flex items-center justify-center gap-2"
            >
              Register Now <ArrowRight size={20} />
            </motion.button>
            <Link to="/training">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 border border-white/20 rounded-full text-white font-semibold text-lg hover:bg-white/10 hover:border-teal-500/40 transition-all"
              >
                View All Programs
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/5 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
              AI Factory
            </Link>
            <p className="text-gray-400 text-sm mt-4">Master the art of AI communication.</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Programs</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-fundamentals" className="hover:text-teal-400 transition">AI Fundamentals</Link></li>
              <li><Link to="/corporate-ai-training" className="hover:text-teal-400 transition">Corporate Training</Link></li>
              <li><Link to="/ai-fellowship" className="hover:text-teal-400 transition">AI Fellowship</Link></li>
              <li><Link to="/student-program" className="hover:text-teal-400 transition">Student Program</Link></li>
              <li><Link to="/prompt-engineering" className="hover:text-teal-400 transition">Prompt Engineering</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/#about" className="hover:text-teal-400 transition">About</Link></li>
              <li><Link to="/training" className="hover:text-teal-400 transition">All Programs</Link></li>
              <li><Link to="/#contact" className="hover:text-teal-400 transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Refund Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-600 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function PromptEngineeringPage() {
  const [registrationModal, setRegistrationModal] = useState(false);

  const handleRegister = () => {
    setRegistrationModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onRegister={handleRegister} />
      <Hero onRegister={handleRegister} />
      <ModulesSection />
      <BenefitsSection />
      <WhoIsThisForSection />
      <JourneySection />
      <FinalCTA onRegister={handleRegister} />
      <Footer />

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={registrationModal}
        onClose={() => setRegistrationModal(false)}
      />
    </div>
  );
}